import { zodResolver } from '@hookform/resolvers/zod'
import { useState } from 'react'
import { useForm, type DefaultValues } from 'react-hook-form'
import { z } from 'zod'

import i18n from '@/i18n'
import { requiredStringWithLabel } from '@/utils/zod'

import { CURRICULUM_AIMS } from '../constants/curriculum-aim'
import { EMPTY_BASKET } from '../constants/curriculum-basket-config'
import { CURRICULUM_BASKET_TYPES } from '../constants/curriculum-basket-type'
import { CURRICULUM_TIME_UNITS } from '../constants/curriculum-time-unit'
import { validateNameUniqueness } from '../utils/validate-name-uniqueness'

type BasicInformationStep = z.infer<typeof basicInformationSchema>

const basicInformationSchema = z.object({
  educationLevel: requiredStringWithLabel(
    i18n.t('label.education-level', { ns: 'curriculumsManagement' })
  ),
  name: requiredStringWithLabel(
    i18n.t('label.name', { ns: 'curriculumsManagement' })
  ).max(80, i18n.t('error.max-length', { ns: 'common', NUMBER: 80 })),
  aim: z.enum(CURRICULUM_AIMS, {
    required_error: i18n.t('error.required-field-with-label', {
      ns: 'common',
      LABEL: i18n.t('label.aim', { ns: 'curriculumsManagement' })
    })
  })
})

const createBasicInformationSchema = (curriculumId?: string) =>
  basicInformationSchema.superRefine(async (data, ctx) => {
    if (!data.name || !data.educationLevel || !data.aim) return

    const isUnique = await validateNameUniqueness({
      name: data.name,
      educationLevel: data.educationLevel,
      aim: data.aim,
      curriculumId
    })

    if (!isUnique) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: i18n.t('help.name-taken', { ns: 'common' }),
        path: ['name']
      })
    }
  })

const semesterSchema = z
  .object({
    hours: z.number().min(1).nullable(),
    isActive: z.boolean()
  })
  .superRefine((semester, ctx) => {
    if (semester.isActive && semester.hours === null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: i18n.t('error.fill-missing-fields', { ns: 'common' }),
        path: ['hours']
      })
    }
  })

export type CurriculumBasketCourse = z.infer<typeof courseSchema>

const courseSchema = z.object({
  course: requiredStringWithLabel(
    i18n.t('label.course', { ns: 'curriculumsManagement' })
  ),
  timeUnit: z.enum(CURRICULUM_TIME_UNITS),
  semesters: z.array(semesterSchema).superRefine((semesters, ctx) => {
    const isAnyFilled = semesters.some(
      semester => semester.isActive || semester.hours !== null
    )

    if (!isAnyFilled) {
      semesters.forEach((semester, index) => {
        Object.keys(semester).forEach(key => {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: i18n.t('error.at-least-one-is-required', { ns: 'common' }),
            path: [index, key]
          })
        })
      })
    }
  })
})

export type CurriculumBasket = z.infer<typeof basketSchema>

const basketSchema = z.object({
  type: z.enum(CURRICULUM_BASKET_TYPES),
  courses: z.array(courseSchema)
})

type BasketsStep = z.infer<typeof basketsSchema>

const basketsSchema = basicInformationSchema.extend({
  baskets: z.array(basketSchema)
})

export type CurriculumFormStep = BasicInformationStep | BasketsStep

export type CurriculumForm = BasketsStep

export const CURRICULUM_FORM_DEFAULT_VALUES = {
  educationLevel: '',
  name: '',
  aim: undefined,
  baskets: [EMPTY_BASKET]
}

const getStepSchema = (step: number, curriculumId?: string) => {
  switch (step) {
    case 0:
      return createBasicInformationSchema(curriculumId)
    case 1:
      return basketsSchema
    default:
      return basketsSchema
  }
}

type Params = {
  defaultValues?: DefaultValues<CurriculumFormStep>
  curriculumId?: string
}

const useCurriculumForm = (params: Params = {}) => {
  const [step, setStep] = useState(0)

  const handleNext = async () => {
    const isValid = await form.trigger()
    if (!isValid) return

    if (step < 2) setStep(prevStep => prevStep + 1)
  }

  const handleBack = () => {
    if (step > 0) setStep(prevStep => prevStep - 1)
  }

  const form = useForm<CurriculumFormStep>({
    mode: 'onBlur',
    defaultValues: params?.defaultValues ?? CURRICULUM_FORM_DEFAULT_VALUES,
    resolver: zodResolver(getStepSchema(step, params?.curriculumId))
  })

  return { form, step, handleNext, handleBack }
}

export default useCurriculumForm
